import {
  BaseControlItem,
  CanopyProvider,
  ControlPanel,
  ControlSection,
  FilterIcon,
} from '@bloomfieldrobotics/canopy';
import { Divider, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useState } from 'react';
import { useUrlReduxBidirectionalSync } from '../../../hooks/useUrlReduxBidirectionalSync';
import { useBlocksByCustomer } from '../../../services/entities/hooks/useBlocksByCustomer';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { BetaWrapper } from '../../commonUI/BetaWrapper/BetaWrapper';
import { CustomerControlSection } from '../../commonUI/ControlSections/CustomerControlSection';
import { LocationControlSection } from '../../commonUI/ControlSections/LocationControlSection';
import { StrictDateRange } from '../../commonUI/DateRangeFilterSelect/types';
import { generateDateRangeFilter } from '../../commonUI/DateRangeFilterSelect/utils/generateDateRangeFilter';
import { useDebouncedLoading } from '../../commonUI/hooks/useDebouncedLoading';
import { MenuPaper } from '../../commonUI/Menu/MenuPaper';
import { useLocationState } from '../../router/hooks/useLocationState';
import {
  selectCurrCustomerID,
  setCurrCustomerByID,
} from '../../select/selectSlice';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ChartsLoadingOverlay } from '../components/ChartsLoadingOverlay';
import { NoDataOverlay } from '../components/NoDataOverlay';
import { ReportChartContainer } from '../components/ReportBarChart/components/ReportChartContainer';
import { BerriesPerClusterSelect } from './components/BerriesPerClusterSelect';
import { ClusterGradingDateRangeSelect } from './components/ClusterGradingDateRangeSelect';
import { ClusterReportChart } from './components/ClusterReportChart/ClusterReportChart';
import { ClusterVarietySelect } from './components/ClusterVarietySelect';
import { DownloadClusterReportButton } from './components/DownloadClusterReportButton';
import { TableViewLauncher } from './components/TableView/TableViewLauncher';
import { DEFAULT_UNIFORMITY_FILTER } from './components/UniformityFilter/constants';
import { UniformityControlItem } from './components/UniformityFilter/UniformityControlItem';
import { useChartDates } from './hooks/useChartDates';
import { useClusterReportPageParams } from './hooks/useClusterReportPageParams/useClusterReportPageParams';
import { useLoadClusterReport } from './hooks/useLoadClusterReport';

export const ClusterGradingReportPage = () => {
  const { dateRangeKey } = useLocationState();
  // TODO: restore this when we build a separate grape block detail
  // const dateRangeOptions = useGrapeReportDateRangeKeys();
  // const navigate = useNavigateWithReportDateRange(
  //   dateRangeOptions,
  //   dateRangeKey
  // );

  const [dateRange, setDateRange] = useState<StrictDateRange | null>(
    generateDateRangeFilter(dateRangeKey ?? 'week')
  );

  // URL parameters for permalinking
  const { params, updateParams } = useClusterReportPageParams();
  const {
    customerID,
    blocksSelected: blockIdsSelected,
    variety,
    berriesPerCluster,
    uniformityFilter,
  } = params;
  const { minSize, maxSize, clusterConsistency } =
    uniformityFilter || DEFAULT_UNIFORMITY_FILTER;

  // Bidirectional sync: URL ⟷ Redux for customerID using the proper hook
  useUrlReduxBidirectionalSync({
    urlValue: customerID,
    updateUrl: (value: string) => updateParams({ customerID: value }),
    reduxSelector: selectCurrCustomerID,
    reduxAction: setCurrCustomerByID,
    defaultValue: '', // Empty string default
  });

  const { blocks, isBlocksLoading } = useBlocksByCustomer(customerID);

  // Filter out nulls and provide default empty array
  const cleanBlockIdsSelected = useMemo(
    () => (blockIdsSelected || []).filter((id): id is string => id !== null),
    [blockIdsSelected]
  );

  // map of ranch id to blocks, filtered by variety (if selected)
  const ranchAndBlockMap = useMemo(() => {
    const map =
      blocks?.reduce((acc, block) => {
        if (variety && block.varietyID !== variety) {
          return acc;
        }
        acc.set(block.ranchID, [...(acc.get(block.ranchID) || []), block]);
        return acc;
      }, new Map<string, Block[]>()) || new Map<string, Block[]>();
    return map;
  }, [blocks, variety]);

  // Default block selection when page first loads
  useEffect(() => {
    if (
      !isBlocksLoading &&
      blocks &&
      (!blockIdsSelected || blockIdsSelected.length === 0)
    ) {
      const allBlockIds = blocks.map((block) => block.id);
      updateParams({
        blocksSelected: allBlockIds,
      });
    }
  }, [blocks, isBlocksLoading, blockIdsSelected, updateParams]);

  // Update block selection when variety filter changes
  useEffect(() => {
    if (
      !isBlocksLoading &&
      blocks &&
      blockIdsSelected &&
      blockIdsSelected.length > 0
    ) {
      let filteredBlockIds: string[];

      if (variety) {
        // Filter blocks to match selected variety
        const filteredBlocks = blocks.filter(
          (block) => block.varietyID === variety
        );
        filteredBlockIds = filteredBlocks.map((block) => block.id);
      } else {
        // No variety filter (show all) - select all blocks
        filteredBlockIds = blocks.map((block) => block.id);
      }

      updateParams({
        blocksSelected: filteredBlockIds,
      });
    }
  }, [blocks, isBlocksLoading, blockIdsSelected, variety, updateParams]);

  // Reconstruct the blocksSelected Map for UI components from the flat array
  const blocksSelectedMap = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!cleanBlockIdsSelected.length) return map;

    // Group selected block IDs by ranch using ranchAndBlockMap
    for (const [ranchId, ranchBlocks] of ranchAndBlockMap.entries()) {
      const selectedBlocksInRanch = ranchBlocks
        .filter((block) => cleanBlockIdsSelected.includes(block.id))
        .map((block) => block.id);

      if (selectedBlocksInRanch.length > 0) {
        map.set(ranchId, selectedBlocksInRanch);
      }
    }
    return map;
  }, [cleanBlockIdsSelected, ranchAndBlockMap]);

  const { t } = useTranslation();

  const { isTableLoading, isClusterReportLoading } = useLoadClusterReport(
    customerID,
    dateRange,
    cleanBlockIdsSelected,
    berriesPerCluster,
    uniformityFilter
  );

  const { chartDates, loading: isChartDatesLoading } =
    useChartDates(isTableLoading);

  const displayLoading = useDebouncedLoading(
    isClusterReportLoading || isChartDatesLoading || isTableLoading
  );

  // Navigate to block detail page when chart bar is clicked
  // const handleBarClick = useCallback(
  //   (index: number, data: DatasetElementType<string | number>[]) => {
  //     const item = data.at(index);
  //     if (item) {
  //       navigate(`/blocks/${item.blockId}/scan/${item.scanId}`);
  //     }
  //   },
  //   [navigate]
  // );

  return (
    <CanopyProvider>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          maxHeight: '100%',
        }}
      >
        <ControlPanel>
          <CustomerControlSection value={customerID} />
          <Divider flexItem />
          <LocationControlSection
            customerId={customerID}
            blocksByRanch={ranchAndBlockMap}
            blocksSelected={blocksSelectedMap}
            onChange={(ranchId, blockIds) => {
              const newBlocksSelectedMap = new Map(blocksSelectedMap);
              newBlocksSelectedMap.set(ranchId, blockIds);
              const flattenedBlockIds = Array.from(
                newBlocksSelectedMap.values()
              ).flat();
              updateParams({ blocksSelected: flattenedBlockIds });
            }}
          />
          <Divider flexItem />
          <ControlSection
            title={t('Feature')}
            icon={<FilterIcon color='secondary' fontSize='small' />}
            flex={2.5}
          >
            <BaseControlItem label={t('Variety')}>
              <ClusterVarietySelect
                customerId={customerID}
                value={variety}
                onChange={(newVariety) => {
                  updateParams({ variety: newVariety });
                }}
              />
            </BaseControlItem>
            <BaseControlItem label={t('Berries per cluster')}>
              <BerriesPerClusterSelect
                value={berriesPerCluster}
                onChange={(newBerriesPerCluster) => {
                  updateParams({ berriesPerCluster: newBerriesPerCluster });
                }}
              />
            </BaseControlItem>
            <UniformityControlItem
              clusterConsistency={clusterConsistency}
              minSize={minSize}
              maxSize={maxSize}
              onSubmit={(clusterConsistency, minSize, maxSize) => {
                const newUniformityFilter = {
                  clusterConsistency,
                  minSize,
                  maxSize,
                };
                updateParams({
                  uniformityFilter: newUniformityFilter,
                });
              }}
            />
          </ControlSection>
          <Divider flexItem />
        </ControlPanel>
        <Stack sx={{ padding: 6, width: '100%' }} spacing={4}>
          <Stack
            direction='row'
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <BetaWrapper>
              <Breadcrumbs
                customPaths={[
                  { label: t('Reports'), path: '/reports' },
                  { label: t('Cluster'), path: '/reports/grape/cluster' },
                ]}
              />
            </BetaWrapper>
            <Stack direction='row' spacing={2}>
              <DownloadClusterReportButton
                customerId={customerID}
                shouldUpdate={!isTableLoading}
                loading={displayLoading}
              />
              <ClusterGradingDateRangeSelect onChange={setDateRange} />
            </Stack>
          </Stack>
          <MenuPaper sx={{ width: '100%' }}>
            {displayLoading ? (
              <ChartsLoadingOverlay />
            ) : chartDates?.length ? (
              <>
                <TableViewLauncher
                  isDataLoading={isTableLoading}
                  customerId={customerID}
                />
                {chartDates?.map(({ scanDate, displayDate }, index) => (
                  <ReportChartContainer
                    key={scanDate}
                    displayDate={displayDate}
                    defaultExpanded={index === 0}
                  >
                    <ClusterReportChart
                      scanDate={scanDate}
                      blockIds={cleanBlockIdsSelected}
                      tableLoading={isTableLoading}
                      //onBarClick={handleBarClick}
                    />
                  </ReportChartContainer>
                ))}
              </>
            ) : (
              <NoDataOverlay />
            )}
          </MenuPaper>
        </Stack>
      </Box>
    </CanopyProvider>
  );
};
